/* eslint-disable no-console */
import { useState, useEffect, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import useMetricsV3Data from './useMetricsV3Data';
import buildSearchOptionsForWidget from './utils/buildSearchOptionsForWidget';
import {
  addMetricsAggregation,
  metricsByGroups,
  metricsByLabels
} from './utils/metrics';
import useMetricsCalculationConfig from 'views/root/views/home/hooks/useMetricsCalculationConfig';
import { formatToCustomMetric } from './utils/formatters';
import useSearchRanges from './CustomWidget/useSearchRanges';
import pick from 'lodash/pick';
import { useSelector } from 'react-redux';
import { myAccountSelector } from 'state/selectors/account';
import { getWidgetCalculationStrategyFromAccountSettings } from './utils/calculationStrategy';
import { isFilteredByLabel } from '../custom-report.util';
import { METRIC_TYPE } from './consts/graphTypes';

const DEFAULT_STATE = {
  data: [],
  avg: {},
  loading: false
};

const GRAPH_DATA_FIELDS = ['metrics', 'filters', 'agg'];

const useWidgetMetricsData = ({
  graph,
  filters,
  metrics,
  metricsTotal,
  loadingMetrics,
  iterations
}) => {
  const { fetchNewV3MetricsDataByFilter } = useMetricsV3Data();
  const filteredByLabel = isFilteredByLabel({
    filters,
    type: graph.metricType
  });

  const [widgetMetrics, setWidgetMetrics] = useState(DEFAULT_STATE);
  const metricsCalculationConfig = useMetricsCalculationConfig();
  const globalDataSnapshot = useRef({});
  const graphDataSnapshot = useRef(graph);
  const searchRanges = useSearchRanges({ filters, iterations });
  const [initialized, setInitialized] = useState(false);
  const account = useSelector(myAccountSelector) || {};
  const aggregationFromSettings =
    getWidgetCalculationStrategyFromAccountSettings({
      account,
      reportName: graph.report,
      widgetName: graph.name,
      reportType: graph.metricType
    });

  const calculatedAgg = aggregationFromSettings ?? graph.agg;

  const metricsWithAggregation = addMetricsAggregation({
    graph: { ...graph, agg: calculatedAgg },
    metricsCalculationConfig
  });

  const customMetrics = formatToCustomMetric({ graph, metricsWithAggregation });

  const updateMetricsData = async ({
    forceFetchMetricsOnWidgetLevel = false
  } = {}) => {
    setWidgetMetrics({
      data: [],
      avg: {},
      loading: true
    });
    try {
      let widgetMetrics = metrics;
      let widgetMetricsAvg = metricsTotal;

      if (forceFetchMetricsOnWidgetLevel || (!metrics && !metricsTotal)) {
        const { metricsOptions, averageOptions } = buildSearchOptionsForWidget({
          metrics: metricsWithAggregation,
          filters: { ...filters, searchRange: searchRanges.timeRange },
          customMetrics
        });
        const widgetData = await fetchNewV3MetricsDataByFilter({
          metricsOptions,
          averageOptions,
          isTotalsNeeded: true,
          filteredByLabel
        });

        widgetMetrics = widgetData?.data;
        widgetMetricsAvg = widgetData?.totals;
      }

      let multiSeriesMetricsData = [];
      if (filteredByLabel) {
        multiSeriesMetricsData = metricsByLabels({
          metrics: widgetMetrics,
          graph,
          filters
        });
      } else {
        multiSeriesMetricsData = metricsByGroups({
          metrics: widgetMetrics,
          graph,
          filters
        });
      }

      setWidgetMetrics({
        data: multiSeriesMetricsData ?? [],
        avg: widgetMetricsAvg?.[0]?.data?.[0] ?? widgetMetricsAvg ?? {},
        loading: false
      });
    } catch {
      setWidgetMetrics({
        data: [],
        avg: {},
        loading: false
      });
    }
    setInitialized(true);
  };

  const metricsGlobalData = { filters, metrics };

  useEffect(() => {
    if (
      !isEqual(
        JSON.stringify(globalDataSnapshot.current),
        JSON.stringify({ filters, metrics })
      ) &&
      searchRanges.timeRange.length
    ) {
      updateMetricsData();
      globalDataSnapshot.current = { filters, metrics };
    }
  }, [JSON.stringify({ metricsGlobalData })]);

  useEffect(() => {
    if (
      graph.metricType !== METRIC_TYPE.goals &&
      initialized &&
      !isEqual(
        JSON.stringify(
          pick(graphDataSnapshot.current ?? {}, GRAPH_DATA_FIELDS)
        ),
        JSON.stringify(pick(graph, GRAPH_DATA_FIELDS))
      )
    ) {
      updateMetricsData({ forceFetchMetricsOnWidgetLevel: true });
      graphDataSnapshot.current = graph;
    }
  }, [JSON.stringify(graph)]);

  const mainMetric = metricsWithAggregation?.[0] ?? {};

  return {
    metricsTotal: widgetMetrics.avg,
    agg: mainMetric.agg,
    data: widgetMetrics.data,
    name: mainMetric.name,
    avgByName: mainMetric.avgBy,
    percentOff: mainMetric.percentOff,
    computedBy: mainMetric.computedBy,
    loading: widgetMetrics.loading || loadingMetrics,
    searchRanges,
    graphWithAggregation: { ...graph, agg: mainMetric.agg }
  };
};

export default useWidgetMetricsData;
